import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, computed } from '@angular/core';
import { Season } from '../../domain/regs-models';

interface SeasonBand {
  leftPct: number;
  widthPct: number;
  color: string;
  shortLabel?: string;
}

@Component({
  selector: 'app-season-strip',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="season-strip">
      <div class="today-marker" [style.left.%]="todayFrac() * 100">
        <span class="today-label">Today</span>
        <div class="today-pin-top"></div>
      </div>

      <div class="strip-bar">
        @for (seg of bands(); track $index) {
          <div
            class="seg-band"
            [style.left.%]="seg.leftPct"
            [style.width.%]="seg.widthPct"
            [style.background-color]="seg.color"
          >
            @if (seg.shortLabel) {
              <span class="seg-label">{{ seg.shortLabel }}</span>
            }
          </div>
        }
        <div class="today-pin-bottom" [style.left.%]="todayFrac() * 100"></div>
      </div>

      <div class="months-row">
        @for (m of months; track $index) {
          <span class="month-char" [style.left.%]="m.pct">{{ m.char }}</span>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .season-strip {
        position: relative;
        width: 100%;
        height: 72px;
        margin: 6px 0 10px;
        user-select: none;
      }
      .today-marker {
        position: absolute;
        top: 0;
        transform: translateX(-50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        z-index: 4;
      }
      .today-label {
        font-size: 12px;
        font-weight: 700;
        color: #1c1c1e;
        white-space: nowrap;
      }
      .today-pin-top {
        width: 2px;
        height: 6px;
        background: #1c1c1e;
        margin-top: 2px;
      }
      .today-pin-bottom {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 2px;
        background: #1c1c1e;
        transform: translateX(-50%);
        z-index: 5;
      }
      .strip-bar {
        position: absolute;
        top: 20px;
        left: 0;
        right: 0;
        height: 30px;
        background: #f2f2f6;
        border-radius: 8px;
        overflow: hidden;
      }
      .seg-band {
        position: absolute;
        top: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-left: 2px solid #ffffff;
      }
      .seg-label {
        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
      }
      .months-row {
        position: absolute;
        top: 54px;
        left: 0;
        right: 0;
        height: 18px;
      }
      .month-char {
        position: absolute;
        font-size: 11px;
        font-weight: 600;
        color: #8a8a8e;
        transform: translateX(-50%);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeasonStripComponent {
  @Input() seasons: Season[] = [];
  @Input() todayMd = '';

  readonly months = [
    { char: 'J', pct: (0 / 365) * 100 + 4 },
    { char: 'F', pct: (31 / 365) * 100 + 4 },
    { char: 'M', pct: (59 / 365) * 100 + 4 },
    { char: 'A', pct: (90 / 365) * 100 + 4 },
    { char: 'M', pct: (120 / 365) * 100 + 4 },
    { char: 'J', pct: (151 / 365) * 100 + 4 },
    { char: 'J', pct: (181 / 365) * 100 + 4 },
    { char: 'A', pct: (212 / 365) * 100 + 4 },
    { char: 'S', pct: (243 / 365) * 100 + 4 },
    { char: 'O', pct: (273 / 365) * 100 + 4 },
    { char: 'N', pct: (304 / 365) * 100 + 4 },
    { char: 'D', pct: (334 / 365) * 100 + 4 },
  ];

  todayFrac(): number {
    return this.dayFrac(this.todayMd);
  }

  private dayFrac(md: string): number {
    if (!md || md.length !== 5) return 0;
    const m = parseInt(md.substring(0, 2), 10) || 1;
    const d = parseInt(md.substring(3), 10) || 1;
    const doy = Math.floor(
      (Date.UTC(2026, m - 1, d) - Date.UTC(2026, 0, 1)) / (24 * 60 * 60 * 1000)
    );
    return Math.max(0, Math.min(1, doy / 365));
  }

  readonly bands = computed<SeasonBand[]>(() => {
    const list: SeasonBand[] = [];
    const open = this.seasons.filter((s) => !s.closed);
    const shut = this.seasons.filter((s) => s.closed);

    if (open.length === 0) {
      list.push({ leftPct: 0, widthPct: 100, color: '#1f9d61' });
    }

    const addBand = (s: Season, color: string) => {
      const a = this.dayFrac(s.from);
      const b = this.dayFrac(s.to) + 1 / 365;
      const parts = a <= b ? [{ a, b }] : [{ a, b: 1.0 }, { a: 0.0, b }];

      for (const p of parts) {
        let short: string | undefined;
        if (!s.closed && s.label) {
          const match = s.label.match(/^\s*(\d{1,3})\s+fish\b/);
          if (match) short = match[1];
        }
        list.push({
          leftPct: p.a * 100,
          widthPct: (p.b - p.a) * 100,
          color,
          shortLabel: short,
        });
      }
    };

    for (const s of open) addBand(s, '#1f9d61');
    for (const s of shut) addBand(s, '#e0596b');

    return list;
  });
}
